import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import useWindowWidth from '../../hooks/useWindowWidth.js';
import { setScroll } from '../../redux/scrollSlice.js';
import { useDispatch, useSelector } from 'react-redux';

import luganeseIcon from '../../static/images/region_icons/luganese-icon.png';
import mendrisiottoIcon from '../../static/images/region_icons/mendrisiotto-icon.png';
import bellinzoneseIcon from '../../static/images/region_icons/bellinzonese-icon.png';
import locarneseIcon from '../../static/images/region_icons/locarnese-icon.png';

import { setNewFilters } from '../../redux/filtersSlice';
// Immagine dell'header della modale di ricerca (asset del cliente, 2026-08-13).
// Il soggetto è a destra: il titolo sta a sinistra, dove l'immagine è cielo e montagne.
import searchHeaderImage from '../../static/images/search_header.jpg';
import tempLogo from '../../static/images/temporary_logo.png';
import LanguageSelector from '../navbar/LanguageSelector.jsx';
import { setIsModalOpen, setSectionOpen } from '../../redux/searchbarSlice.js';
import { useTranslation } from 'react-i18next';
import StringSearch from './StringSearch.jsx';

// Riga compatta di una sezione non aperta della modale (solo mobile: su desktop
// le sezioni non attive sono `md:hidden`). Icona + etichetta + chevron, come da
// mockup cliente 2026-07-30.
const CollapsedRow = ({ icon, label, isSet, onOpen, onClear }) => (
    <div className='flex items-center gap-2'>
        {
            isSet &&
            <div className='w-fit rounded-xl cursor-pointer border pt-2 px-2 text-[#225424]' onClick={onClear}>
                <i className="fi fi-rr-circle-xmark text-2xl"></i>
            </div>
        }
        <div
            className={`flex-grow flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${isSet ? 'font-bold text-[#225424]' : ''}`}
            onClick={onOpen}
        >
            <i className={`${icon} text-xl leading-none text-[#225424] mt-1`}></i>
            <div className='flex-grow'>{label}</div>
            <i className="fi fi-rr-angle-small-right text-xl leading-none mt-1"></i>
        </div>
    </div>
);

const SearchBar = ({ isStringSearch = false }) => {

    const { t } = useTranslation();
    const location = useLocation();
    const url = location.pathname;

    // Monitoro lo status della SearchBar memorizzato in Redux
    // Lo status gestisce i booleani di visibilità, apertura, chiusura eccetera, non gestisce i dati della SearchBar
    const { isModalOpen, sectionOpen } = useSelector(state => state.searchbar.modal);

    const windowWidth = useWindowWidth();

    // Check su filters (corrisponde sempre all'oggetto che viene spedito in backend)
    const filters = useSelector(state => state.filters)

    // State della stringa di ricerca
    const [isStringSearchOpen, setIsStringSearchOpen] = useState(false);

    // Gestisco il click sulle tre voci
    const dispatch = useDispatch();
    const handleFiltersClick = (string) => {
        dispatch(setIsModalOpen(true));
        dispatch(setSectionOpen(string));
    }

    // Gestisco lo stop dello scroll
    useEffect(() => {
        if (windowWidth < 768 && isModalOpen) {
            dispatch(setScroll(false))
        }
    }, [windowWidth, isModalOpen, dispatch])

    // Su mobile la modale entra da destra; su desktop è un pannello inline sotto la barra
    // e resta senza animazione (invariato per richiesta cliente).
    const modalMotion = windowWidth < 768
        ? {
            initial: { x: '100%' },
            animate: { x: 0 },
            exit: { x: '100%' },
            transition: { type: 'tween', duration: 0.28, ease: 'easeOut' }
        }
        : { initial: false };

    // Gestisco la chiusura della modale
    const handleCloseModal = () => {
        if (windowWidth < 768) {
            dispatch(setSectionOpen(null));
            dispatch(setIsModalOpen(false));
            dispatch(setScroll(true));
        } else {
            dispatch(setIsModalOpen(false));
            dispatch(setSectionOpen(null))
        }
    }

    // Gestisco la scelta del where
    const handleRegionClick = (region) => {
        if (filters.region.includes(region)) {
            dispatch(setNewFilters({ region: filters.region.filter(element => element !== region) }))
        } else {
            dispatch(setNewFilters({ region: [...filters.region, region] }))
        }
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco la scelta del who
    const handlePeopleClick = (people) => {
        if (filters.groupModel.includes(people)) {
            dispatch(setNewFilters({ groupModel: filters.groupModel.filter(element => element !== people) }))
        } else {
            dispatch(setNewFilters({ groupModel: [...filters.groupModel, people] }))
        }
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco la scelta del when
    const handleMonthClick = (month) => {
        if (filters.availablePeriod.includes(month)) {
            dispatch(setNewFilters({ availablePeriod: filters.availablePeriod.filter(element => element !== month) }))
        } else {
            dispatch(setNewFilters({ availablePeriod: [...filters.availablePeriod, month] }))
        }
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco il button finale
    const navigate = useNavigate();
    const goToActivities = () => {
        dispatch(setIsModalOpen(false))
        dispatch(setScroll(true))
        dispatch(setSectionOpen(null))
        if (url === '/') navigate("/all-activities");
    }

    useEffect(() => {
        // Questo useEffect rimanda ad All-Activities in automatico dopo una selezione in modalità desktop
        // dato che manca il button
        if ((url === '/' && (filters.region?.length > 0 || filters.groupModel?.length > 0 || filters.availablePeriod?.length > 0)) || url === '/all-activities') {
            if (windowWidth > 768) {
                goToActivities()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    return (
        <div className="w-full flex flex-col items-center gap-2 px-4">
            <div className={`bg-white rounded-xl text-neutral-400 w-full md:w-[600px] flex gap-4 flex-col items-center md:flex-row shadow-lg p-2`}>
                {/* La ricerca testuale espansa prende il posto delle tre voci con un cross-fade.
                    `mode="wait"` evita la sovrapposizione dei due layout durante la transizione. */}
                <AnimatePresence mode="wait" initial={false}>
                {
                    isStringSearch && isStringSearchOpen ?
                    <motion.div
                        key="string-search"
                        className="w-full"
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        transition={{ duration: 0.15 }}
                    >
                        <StringSearch onClose={() => setIsStringSearchOpen(false)} />
                    </motion.div>
                    :
                <motion.div
                    key="search-options"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className={`
                    w-full min-w-0 flex flex-nowrap justify-center items-center
                    ${windowWidth < 380 ? 'flex-col' : 'flex-row'}
                    gap-x-2 md:gap-x-6
                    `}>

                    {/* Dove */}
                    <div className="flex items-center min-w-0">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer items-center hover:bg-[#e2eeed] ${sectionOpen === 'where' && 'bg-[#e2eeed]'}`}>
                            {
                                filters.region && filters.region.length > 0 ? (
                                    <div className='
                                        text-black text-xs
                                        border py-1 px-2 md:px-3 rounded font-bold
                                        flex gap-x-2 items-center
                                    '>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => { dispatch(setNewFilters({ region: [] })) }}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div
                                            onClick={() => handleFiltersClick('where')}
                                            className='
                                            max-w-[60px] md:max-w-[120px]
                                            whitespace-nowrap overflow-hidden text-ellipsis
                                            '
                                        >
                                            {filters.region.join(', ')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('where')}>{t('poolBar.SearchBar.where')}</div>
                                )
                            }
                        </div>
                    </div>
                    {/* Con chi */}
                    <div className="flex items-center min-w-0">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer hover:bg-[#e2eeed] ${sectionOpen === "who" && 'bg-[#e2eeed]'}`}>
                            {
                                filters.groupModel && filters.groupModel.length > 0 ? (
                                    <div className='
                                        text-black text-xs
                                        border py-1 px-2 md:px-3 rounded font-bold
                                        flex gap-x-2 items-center
                                    '>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => { dispatch(setNewFilters({ groupModel: [] })) }}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div
                                            onClick={() => handleFiltersClick('who')}
                                            className='
                                        max-w-[60px] md:max-w-[120px]
                                        whitespace-nowrap overflow-hidden text-ellipsis
                                        '
                                        >
                                            {filters.groupModel.join(', ')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('who')}>{t('poolBar.SearchBar.who')}</div>
                                )
                            }
                        </div>
                    </div>

                    {/* Quando */}
                    <div className="flex items-center min-w-0">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer hover:bg-[#e2eeed] ${sectionOpen === "when" && 'bg-[#e2eeed]'}`}>
                            {
                                filters.availablePeriod && filters.availablePeriod.length > 0 ? (
                                    <div className='
                                        text-black text-xs
                                        border py-1 px-2 md:px-3 rounded font-bold
                                        flex gap-x-2 items-center
                                    '>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => { dispatch(setNewFilters({ availablePeriod: [] })) }}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div
                                            onClick={() => handleFiltersClick('when')}
                                            className='
                                        max-w-[60px] md:max-w-[120px]
                                        whitespace-nowrap overflow-hidden text-ellipsis
                                        '
                                        >
                                            {filters.availablePeriod.join(', ')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('when')}>{t('poolBar.SearchBar.when')}</div>
                                )
                            }
                        </div>
                    </div>

                    {/* String Search: lente verde + label "Cerca" per distinguerla
                        dall'invio della ricerca a tre domande (richiesta cliente 2026-07-30) */}
                    {
                        isStringSearch &&
                        <div
                            className='flex items-center gap-1.5 py-3 px-1 cursor-pointer whitespace-nowrap shrink-0'
                            onClick={() => setIsStringSearchOpen(true)}
                        >
                            <span className="flex items-center justify-center w-6 h-6">
                                <i className="fi fi-br-search text-[#225424] text-lg leading-none"></i>
                            </span>
                            <span className="text-[#225424] font-medium text-sm md:text-lg">{t('StringSearch.button')}</span>
                        </div>
                    }
                </motion.div>
                }
                </AnimatePresence>
            </div>

            {/* Open modal.
                Su mobile è un pannello che scende dall'alto e si ferma sopra la BaseBar
                (80px), che resta visibile e cliccabile: il suo tasto "indietro" è l'unico
                modo di chiuderla (vedi BaseBar.jsx), non c'è una X propria.
                Il padding superiore segue il bordo di sicurezza globale dell'app (`body` in
                index.css): essendo `fixed`, la modale ignora il padding del body e senza questo
                il contenuto finirebbe sotto la status bar in app nativa. */}
            <AnimatePresence>
            {
                (isModalOpen) &&
                <motion.div
                    key="search-modal"
                    {...modalMotion}
                    className={`fixed z-40 left-0 top-0 right-0 bottom-[80px] overflow-y-auto md:w-[600px] md:static md:bottom-auto md:overflow-visible md:rounded-xl md-w-fit flex flex-col gap-2 bg-white pt-[calc(max(env(safe-area-inset-top),20px)+0.5rem)] md:pt-0`}>
                    {/* Barra superiore (solo mobile): la modale copre la Navbar, quindi logo e
                        cambio lingua vanno ricreati qui. Come da mockup stanno SOPRA l'immagine,
                        su sfondo bianco, non sovrapposti alla foto. */}
                    <div className='flex items-center justify-between gap-2 px-4 pb-2 shrink-0 md:hidden'>
                        <img src={tempLogo} alt="TicinoWild Logo" className='w-[150px]' />
                        <LanguageSelector />
                    </div>

                    {/* Header hero (solo mobile): immagine a tutta larghezza, senza velature.
                        L'unica sfumatura è nei 64px in fondo, per fondere la foto col pannello.
                        Il titolo è largo poco più di metà così va su tre righe e lascia libero
                        il tasso sulla destra. L'object-position spostato a destra (85%) tiene il
                        muso del tasso dentro l'inquadratura anche sugli schermi stretti, dove il
                        crop centrato di object-cover lo taglierebbe nell'angolo. */}
                    <div className='relative h-[300px] shrink-0 overflow-hidden md:hidden'>
                        <img src={searchHeaderImage} alt="" className='absolute inset-0 w-full h-full object-cover object-[85%_50%]' />
                        <div className='absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent'></div>
                        <h3 className='absolute left-5 top-6 w-[55%] text-[#225424] leading-tight'>
                            {t('poolBar.SearchBar.modalTitle')}
                        </h3>
                    </div>

                    {/* Chiudi (desktop): la modale è un pannello inline, senza header hero */}
                    <div className="w-fit rounded-xl cursor-pointer p-2 self-start hidden md:block" onClick={handleCloseModal}>
                        <i className="fi fi-rr-circle-xmark text-[#225424] text-4xl"></i>
                    </div>
                    {/* Su mobile il corpo dei filtri è una card che si sovrappone all'immagine:
                        angoli superiori arrotondati, risalita di 24px e ombra verso l'alto.
                        NB: la curva si vede solo nel tratto sovrapposto alla foto (card bianca su
                        pannello bianco), quindi il raggio percepito è ~24px — scelta accettata.
                        Su desktop torna un semplice contenitore (l'header hero non esiste). */}
                    <div className="relative z-10 -mt-6 rounded-t-[5.5rem] shadow-[0_-6px_20px_rgba(0,0,0,0.10)] md:mt-0 md:rounded-xl md:shadow-none flex flex-col gap-2 bg-white">
                        <div className='flex-grow flex flex-col pt-2 gap-2 bg-white rounded-xl'>

                            {/* WHERE */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'where' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'where' ? (
                                        <div className='bg-white rounded-xl'>
                                            <h4>{t('poolBar.SearchBar.modalWhereTitle')}</h4>
                                            <div className="flex gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${filters.region.includes('Ascona-Locarno') ? 'bg-[#e2eeed]' : ''}`} src={locarneseIcon} alt="Ascona-Locarno" onClick={() => handleRegionClick('Ascona-Locarno')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${filters.region.includes('Bellinzona e Valli') ? 'bg-[#e2eeed]' : ''}`} src={bellinzoneseIcon} alt="Bellinzona e Valli" onClick={() => handleRegionClick('Bellinzona e Valli')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${filters.region.includes('Luganese') ? 'bg-[#e2eeed]' : ''}`} src={luganeseIcon} alt="Luganese" onClick={() => handleRegionClick('Luganese')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${filters.region.includes('Mendrisiotto') ? 'bg-[#e2eeed]' : ''}`} src={mendrisiottoIcon} alt="Mendrisiotto" onClick={() => handleRegionClick('Mendrisiotto')} />
                                            </div>
                                        </div>
                                    ) : (
                                        <CollapsedRow
                                            icon="fi fi-rr-marker"
                                            label={filters.region.length > 0 ? filters.region.join(', ') : t('poolBar.SearchBar.modalWhere')}
                                            isSet={filters.region.length > 0}
                                            onOpen={() => handleFiltersClick('where')}
                                            onClear={() => dispatch(setNewFilters({ region: [] }))}
                                        />
                                    )

                                }
                            </div>

                            {/* WHO */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'who' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'who' ? (
                                        <div>
                                            <h4>{t('poolBar.SearchBar.modalWhoTitle')}</h4>
                                            <div className='flex gap-2 text-[8pt] md:text-sm leading-none text-center overflow-x-auto whitespace-nowrap hide-scrollbar'>
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${filters.groupModel.includes('Da soli') ? 'bg-[#e2eeed]' : ''} flex flex-col items-center`} onClick={() => handlePeopleClick('Da soli')}>
                                                    <i className="fi fi-rr-employee-man-alt text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoAlone')}</div>
                                                </div> {/* Alone */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${filters.groupModel.includes('In coppia') ? 'bg-[#e2eeed]' : ''} flex flex-col items-center`} onClick={() => handlePeopleClick('In coppia')}>
                                                    <i className="fi fi-rr-people text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoCouple')}</div>
                                                </div> {/* Couple */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${filters.groupModel.includes('Fra amici') ? 'bg-[#e2eeed]' : ''} flex flex-col items-center`} onClick={() => handlePeopleClick('Fra amici')}>
                                                    <i className="fi fi-rr-users-alt text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoFriends')}</div>
                                                </div> {/* Friends */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${filters.groupModel.includes('In famiglia') ? 'bg-[#e2eeed]' : ''} flex flex-col items-center`} onClick={() => handlePeopleClick('In famiglia')}>
                                                    <i className="fi fi-rr-family text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoFamily')}</div>
                                                </div> {/* Family */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${filters.groupModel.includes('Per bambini') ? 'bg-[#e2eeed]' : ''} flex flex-col items-center`} onClick={() => handlePeopleClick('Per bambini')}>
                                                    <i className="fi fi-rr-man-head text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoKids')}</div>
                                                </div> {/* Kids */}
                                            </div>
                                        </div>
                                    ) : (
                                        <CollapsedRow
                                            icon="fi fi-rr-users-alt"
                                            label={filters.groupModel.length > 0 ? filters.groupModel.join(', ') : t('poolBar.SearchBar.modalWho')}
                                            isSet={filters.groupModel.length > 0}
                                            onOpen={() => handleFiltersClick('who')}
                                            onClear={() => dispatch(setNewFilters({ groupModel: [] }))}
                                        />
                                    )

                                }
                            </div>

                            {/* WHEN */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'when' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'when' ? (
                                        <div>
                                            <h4>{t('poolBar.SearchBar.modalWhenTitle')}</h4>
                                            <div className='grid grid-cols-3'>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Gennaio') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Gennaio')}>{t('poolBar.SearchBar.modalMonths.january')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Febbraio') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Febbraio')}>{t('poolBar.SearchBar.modalMonths.febrary')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Marzo') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Marzo')}>{t('poolBar.SearchBar.modalMonths.march')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Aprile') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Aprile')}>{t('poolBar.SearchBar.modalMonths.april')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Maggio') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Maggio')}>{t('poolBar.SearchBar.modalMonths.may')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Giugno') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Giugno')}>{t('poolBar.SearchBar.modalMonths.june')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Luglio') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Luglio')}>{t('poolBar.SearchBar.modalMonths.july')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Agosto') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Agosto')}>{t('poolBar.SearchBar.modalMonths.august')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Settembre') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Settembre')}>{t('poolBar.SearchBar.modalMonths.september')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Ottobre') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Ottobre')}>{t('poolBar.SearchBar.modalMonths.october')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Novembre') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Novembre')}>{t('poolBar.SearchBar.modalMonths.november')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${filters.availablePeriod.includes('Dicembre') ? 'bg-[#e2eeed]' : ''}`} onClick={() => handleMonthClick('Dicembre')}>{t('poolBar.SearchBar.modalMonths.december')}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <CollapsedRow
                                            icon="fi fi-rr-calendar"
                                            label={filters.availablePeriod.length > 0 ? filters.availablePeriod.join(', ') : t('poolBar.SearchBar.modalWhen')}
                                            isSet={filters.availablePeriod.length > 0}
                                            onOpen={() => handleFiltersClick('when')}
                                            onClear={() => dispatch(setNewFilters({ availablePeriod: [] }))}
                                        />
                                    )

                                }
                            </div>

                            {/* Button */}
                            <button className='bg-[#225424] mx-2 my-2 py-3 px-4 text-white rounded-xl flex gap-2 items-center justify-center md:hidden' onClick={goToActivities}>
                                <i className="fi fi-rr-search mt-1"></i>
                                <div>{t('poolBar.SearchBar.modalButton')}</div>
                            </button>
                        </div>
                    </div>
                </motion.div>
            }
            </AnimatePresence>

        </div >

    )
}

export default SearchBar;